import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Copy, FileText, Sparkles, ArrowUpRight, Loader2, Download, Lock, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { http, shareUrl } from "@/lib/api";
import { RadarCard, MeterCard } from "@/components/ProfileCharts";
import { MindMap } from "@/components/MindMap";
import { DownloadModal } from "@/components/DownloadModal";
import { GrowthSection } from "@/components/GrowthSection";
import { useLang } from "@/i18n";
import { EmailResultsCard } from "@/components/EmailResultsCard";
import { GoalsPanel } from "@/components/GoalsPanel";
import { StrengthsCardDialog } from "@/components/StrengthsCardDialog";
import { ShortlistPanel } from "@/components/ShortlistPanel";

export const useResult = (token) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => http.get(`/results/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.status || 500));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, err, reload: load };
};

const ModeToggle = ({ mode, setMode, aiPaid }) => (
  <div className="inline-flex rounded-full bg-white border border-[#1C242B]/12 p-1" data-testid="evaluation-mode-toggle">
    {[["static", "Static evaluation", FileText], ["ai", "AI evaluation", Sparkles]].map(([k, l, I]) => (
      <button key={k} data-testid={`mode-${k}-btn`} onClick={() => setMode(k)}
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${mode === k ? "bg-[#1C242B] text-white" : "text-slate-600 hover:text-[#1C242B]"}`}>
        <I size={14} /> {l} {k === "ai" && !aiPaid && <Lock size={12} className="opacity-60" />}
      </button>
    ))}
  </div>
);

export default function Results() {
  const { token } = useParams();
  const { data, err } = useResult(token);
  const [mode, setMode] = useState("static");
  const [modal, setModal] = useState(false);
  const { t } = useLang();

  if (err) return <div className="max-w-3xl mx-auto p-10" data-testid="results-error">{err === 409 ? <>This assessment isn't finished yet. <Link className="underline" to="/assessment">Continue it</Link>.</> : "Result not found."}</div>;
  if (!data) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#D96B43]" /></div>;

  const { result: r, ai_paid, ai_status } = data;
  const p = r.profile, st = r.strengths;
  const copy = () => { navigator.clipboard.writeText(shareUrl(token)); toast.success("Private link copied. Keep it safe, anyone with it can view your results."); };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-10">
      <section className="grid grid-cols-12 gap-6 fade-up">
        <div className="col-span-12 lg:col-span-8">
          <p className="overline-label">Your results</p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">
            Holland code <span className="text-[#D96B43]" data-testid="holland-code">{p.holland_code}</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl">
            Your strongest aptitudes are <b>{st.top_aptitudes.map((a) => a.label.toLowerCase()).join(", ")}</b>. Your leading intelligences are{" "}
            <b>{st.top_intelligences.map((a) => t(a.label)).join(", ")}</b>, and your top career clusters are <b>{r.clusters.slice(0, 3).map((c) => c.label).join(", ")}</b>.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <ModeToggle mode={mode} setMode={setMode} aiPaid={ai_paid} />
            <button data-testid="copy-private-link-btn" onClick={copy} className="inline-flex items-center gap-2 rounded-full border border-[#1C242B]/15 bg-white px-4 py-2 text-sm hover:border-[#1C242B]/40 transition-colors">
              <Copy size={14} /> Copy private link
            </button>
            <Link to={`/assessment?retake=${token}`} data-testid="retake-test-btn" className="inline-flex items-center gap-2 rounded-full border border-[#1C242B]/15 bg-white px-4 py-2 text-sm hover:border-[#1C242B]/40 transition-colors">
              <RotateCcw size={14} /> Retake &amp; compare
            </Link>
            <StrengthsCardDialog result={r} />
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 rounded-2xl bg-[#1C242B] text-[#FAF8F5] p-7 flex flex-col justify-between">
          <div>
            <p className="font-mono-ui text-xs tracking-[0.2em] text-[#F0A584]">{mode === "ai" ? "AI EVALUATION" : "STATIC EVALUATION"}</p>
            {mode === "static" ? (
              <p className="mt-3 text-slate-300 text-sm leading-relaxed">Rule-based scoring of all 122 answers. Fully free: view the complete report on-screen or download the PDF for any amount, including $0.</p>
            ) : ai_paid ? (
              <p className="mt-3 text-slate-300 text-sm leading-relaxed" data-testid="ai-status-text">AI analysis status: <b className="text-white">{ai_status}</b>. Open the full report to read it.</p>
            ) : (
              <p className="mt-3 text-slate-300 text-sm leading-relaxed">A premium AI model writes a personal narrative, blind spots, hidden strengths and a 90-day plan. A small contribution (from $3) covers its running cost.</p>
            )}
          </div>
          <div className="mt-6 grid gap-2.5">
            <Link to={`/results/${token}/report`} data-testid="open-report-btn" className="inline-flex justify-center items-center gap-2 rounded-full bg-white text-[#1C242B] px-5 py-3 text-sm font-semibold hover:bg-[#F0A584] transition-colors">
              Open detailed report <ArrowUpRight size={16} />
            </Link>
            <button data-testid="open-download-modal-btn" onClick={() => setModal(true)} className="inline-flex justify-center items-center gap-2 rounded-full bg-[#D96B43] text-white px-5 py-3 text-sm font-semibold hover:bg-[#C05630] transition-colors">
              {mode === "ai" && !ai_paid ? <><Sparkles size={16} /> Unlock AI evaluation</> : <><Download size={16} /> Download PDF</>}
            </button>
          </div>
        </div>
      </section>

      <section className="mt-12 grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-5"><RadarCard testId="intelligence-radar" title="Multiple intelligences" data={p.intelligences} /></div>
        <div className="col-span-12 lg:col-span-7"><MeterCard testId="aptitude-meters" title="Aptitude test performance" items={Object.values(p.sections).map((s) => [s.title, s.pct])} /></div>
        <div className="col-span-12 md:col-span-6 lg:col-span-4"><RadarCard testId="reasoning-radar" title="Reasoning types" data={p.reasoning} color="#3E6B5C" /></div>
        <div className="col-span-12 md:col-span-6 lg:col-span-4"><RadarCard testId="riasec-radar" title="Interests (RIASEC)" data={Object.fromEntries(Object.entries(p.riasec).map(([k, v]) => [r.riasec_names[k], v]))} color="#D97706" /></div>
        <div className="col-span-12 lg:col-span-4"><MeterCard testId="knowledge-meters" title="Knowledge bases" color="#2563EB" items={Object.entries(p.dims).filter(([k]) => k.startsWith("k_")).map(([k, v]) => [k.slice(2).replace(/^./, (c) => c.toUpperCase()), v])} /></div>
        <div className="col-span-12"><MeterCard testId="workstyle-meters" title="Work style, social preference & values" color="#3E6B5C" items={Object.values(st.work_style).map((w) => [w.label, w.score])} /></div>
      </section>

      <ShortlistPanel token={token} careers={r.careers} />

      <GrowthSection token={token} favorites={data.favorites} />

      <section className="mt-10 grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-6"><GoalsPanel token={token} profile={p} initialGoals={data.goals} /></div>
        <div className="col-span-12 lg:col-span-6"><EmailResultsCard token={token} savedEmail={data.email} reminderAt={data.reminder_at} /></div>
      </section>

      <section className="mt-16">
        <p className="overline-label">Career mind map</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2 mb-6">{r.clusters.length} disciplinary structures, {r.careers.length} careers, mapped to you.</h2>
        <MindMap token={token} careers={r.careers} clusters={r.clusters} />
      </section>

      <section className="mt-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4">
          <p className="overline-label">Narrowed down</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">Your top disciplines</h2>
          <ol className="mt-6 space-y-3">
            {r.clusters.slice(0, 5).map((c, i) => (
              <li key={c.id} data-testid={`top-cluster-${i + 1}`} className="flex items-center gap-4 rounded-xl bg-white border border-[#1C242B]/10 p-4">
                <span className="w-2 self-stretch rounded-full" style={{ background: c.color }} />
                <span className="flex-1 font-display font-medium">{c.label}</span>
                <span className="font-mono-ui text-sm text-[#D96B43]">{c.score}%</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="col-span-12 lg:col-span-8">
          <p className="overline-label">Pick a career</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">Top 10 matches, and why</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {r.careers.slice(0, 10).map((c, i) => (
              <Link key={c.id} to={`/results/${token}/career/${c.id}`} data-testid={`top-career-${c.id}`} className="lift rounded-2xl bg-white border border-[#1C242B]/10 p-5 block">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="font-mono-ui text-xs text-slate-400">#{i + 1}</p>
                    <h3 className="font-display text-lg font-semibold">{c.title}</h3>
                  </div>
                  <span className="font-mono-ui text-xl font-semibold text-[#D96B43]">{c.score}%</span>
                </div>
                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{c.reasons[0]}</p>
                <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
                  <span className="rounded-full bg-[#E8F0EC] text-[#3E6B5C] px-2 py-0.5">Public: {c.public}</span>
                  <span className="rounded-full bg-[#E8F0EC] text-[#3E6B5C] px-2 py-0.5">People: {c.people}</span>
                  <span className="rounded-full bg-[#F6E7DF] text-[#C05630] px-2 py-0.5">Physical: {c.physical}</span>
                  {c.remote && c.remote !== "On-site" && <span className="rounded-full bg-[#E0EEF0] text-[#0F766E] px-2 py-0.5">{c.remote}</span>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <DownloadModal open={modal} onOpenChange={setModal} token={token} pricing={data.pricing} aiPaid={ai_paid} aiReady={ai_status === "ready"} defaultType={mode} key={mode + String(modal)} />
    </div>
  );
}
